import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { useToast } from '../context/ToastContext';

const CURRENCIES = ['USD', 'EGP', 'EUR', 'SAR', 'AED'];

const ServiceModal = ({ initial, onClose, onSave }) => {
  const [form, setForm] = useState(initial || { name: '', description: '', defaultPrice: '', currency: 'USD', isActive: true });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    onSave({ ...form, defaultPrice: Number(form.defaultPrice || 0) });
  };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <div className="modal-title">{initial ? 'Edit Service' : 'New Service'}</div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <form onSubmit={submit}>
            <div className="input-group">
              <label className="input-label">Name *</label>
              <input className="input" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Website Design, Social Media..." />
            </div>
            <div className="input-group">
              <label className="input-label">Description</label>
              <textarea className="input" rows={4} value={form.description || ''} onChange={e => set('description', e.target.value)} placeholder="What is included in this service..." />
            </div>
            <div className="grid-2">
              <div className="input-group">
                <label className="input-label">Default Price</label>
                <input className="input" type="number" min="0" step="0.01" value={form.defaultPrice || ''} onChange={e => set('defaultPrice', e.target.value)} />
              </div>
              <div className="input-group">
                <label className="input-label">Currency</label>
                <select className="input select" value={form.currency || 'USD'} onChange={e => set('currency', e.target.value)}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18, fontSize: 13 }}>
              <input type="checkbox" checked={form.isActive !== false} onChange={e => set('isActive', e.target.checked)} />
              Active
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary"><Plus size={14} /> Save</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const PackageModal = ({ services, initial, onClose, onSave }) => {
  const [form, setForm] = useState(initial || { name: '', description: '', currency: 'USD', items: [], isActive: true });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addItem = () => set('items', [...(form.items || []), { description: '', quantity: 1, unitPrice: 0 }]);
  const updateItem = (i, key, value) => set('items', form.items.map((item, idx) => idx === i ? { ...item, [key]: value } : item));
  const pickService = (i, serviceId) => {
    const svc = services.find(s => s.id === serviceId);
    updateItem(i, 'serviceId', serviceId);
    if (svc) set('items', form.items.map((item, idx) => idx === i ? { ...item, serviceId, description: svc.name, details: svc.description || '', unitPrice: Number(svc.defaultPrice || 0) } : item));
  };
  const total = (form.items || []).reduce((sum, item) => sum + Number(item.quantity || 1) * Number(item.unitPrice || 0), 0);
  const submit = (e) => { e.preventDefault(); onSave({ ...form, total }); };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 720 }}>
        <div className="modal-header"><div className="modal-title">{initial ? 'Edit Package' : 'New Package'}</div><button className="modal-close" onClick={onClose}><X size={20} /></button></div>
        <div className="modal-body">
          <form onSubmit={submit}>
            <div className="grid-2">
              <div className="input-group"><label className="input-label">Package Name *</label><input className="input" required value={form.name} onChange={e => set('name', e.target.value)} /></div>
              <div className="input-group"><label className="input-label">Currency</label><select className="input select" value={form.currency} onChange={e => set('currency', e.target.value)}>{CURRENCIES.map(c => <option key={c}>{c}</option>)}</select></div>
            </div>
            <div className="input-group"><label className="input-label">Description</label><textarea className="input" rows={2} value={form.description || ''} onChange={e => set('description', e.target.value)} /></div>
            <div className="flex-between mb-16"><div className="card-title">Package Services</div><button type="button" className="btn btn-secondary btn-sm" onClick={addItem}><Plus size={13} /> Add Item</button></div>
            {(form.items || []).map((item, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px 120px 34px', gap: 8, marginBottom: 8 }}>
                <select className="input select" value={item.serviceId || ''} onChange={e => pickService(i, e.target.value)}><option value="">Custom</option>{services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
                <input className="input" value={item.description || ''} onChange={e => updateItem(i, 'description', e.target.value)} placeholder="Service name" />
                <input className="input" type="number" min="1" value={item.quantity || 1} onChange={e => updateItem(i, 'quantity', e.target.value)} />
                <input className="input" type="number" min="0" step="0.01" value={item.unitPrice || ''} onChange={e => updateItem(i, 'unitPrice', e.target.value)} />
                <button type="button" onClick={() => set('items', form.items.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', color: '#ef4444' }}><X size={15} /></button>
              </div>
            ))}
            <div className="flex-between mb-16"><strong>Total: {form.currency} {total.toLocaleString()}</strong><label style={{ display: 'flex', gap: 8 }}><input type="checkbox" checked={form.isActive !== false} onChange={e => set('isActive', e.target.checked)} /> Active</label></div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary">Save Package</button></div>
          </form>
        </div>
      </div>
    </div>
  );
};

const Services = () => {
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [modal, setModal] = useState(null);
  const [packageModal, setPackageModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  const load = () => Promise.all([axios.get('/api/services'), axios.get('/api/services/packages')])
    .then(([s, p]) => { setServices(s.data.data); setPackages(p.data.data); })
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    try {
      if (modal?.service) await axios.put(`/api/services/${modal.service.id}`, payload);
      else await axios.post('/api/services', payload);
      showToast('Service saved', 'success');
      setModal(null);
      load();
    } catch (err) { showToast(err.response?.data?.message || 'Failed to save service', 'error'); }
  };

  const remove = async (service) => {
    if (!window.confirm(`Delete service "${service.name}"? Existing client quotations will keep their copied service data.`)) return;
    try {
      await axios.delete(`/api/services/${service.id}`);
      showToast('Service deleted', 'success');
      load();
    } catch (err) { showToast(err.response?.data?.message || 'Failed to delete service', 'error'); }
  };

  const savePackage = async (payload) => {
    try {
      if (packageModal?.package) await axios.put(`/api/services/packages/${packageModal.package.id}`, payload);
      else await axios.post('/api/services/packages', payload);
      showToast('Package saved', 'success');
      setPackageModal(null);
      load();
    } catch (err) { showToast(err.response?.data?.message || 'Failed to save package', 'error'); }
  };

  const removePackage = async (pack) => {
    if (!window.confirm(`Delete package "${pack.name}"?`)) return;
    try { await axios.delete(`/api/services/packages/${pack.id}`); load(); }
    catch (err) { showToast(err.response?.data?.message || 'Failed to delete package', 'error'); }
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading...</div>;

  return (
    <div className="page-content">
      {modal && <ServiceModal initial={modal.service} onClose={() => setModal(null)} onSave={save} />}
      {packageModal && <PackageModal services={services} initial={packageModal.package} onClose={() => setPackageModal(null)} onSave={savePackage} />}
      <div className="flex-between mb-24">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>Services</h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{services.length} services in catalog</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setPackageModal({})}><Plus size={16} /> New Package</button>
          <button className="btn btn-primary" onClick={() => setModal({})}><Plus size={16} /> New Service</button>
        </div>
      </div>
      <div className="card mb-24" style={{ padding: 0 }}>
        <div style={{ padding: 18 }}><div className="card-title">Service Packages</div><p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Ready-made offers like Startup Branding or Monthly Social Media.</p></div>
        <div className="table-wrap"><table><thead><tr><th>Name</th><th>Items</th><th>Total</th><th>Status</th><th>Actions</th></tr></thead><tbody>
          {packages.map(p => <tr key={p.id}><td style={{ fontWeight: 700 }}>{p.name}<div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{p.description}</div></td><td>{(p.items || []).length}</td><td>{p.currency} {Number(p.total || 0).toLocaleString()}</td><td><span className={`badge ${p.isActive ? 'badge-green' : 'badge-gray'}`}>{p.isActive ? 'Active' : 'Inactive'}</span></td><td><button onClick={() => setPackageModal({ package: p })} title="Edit" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><Pencil size={14} /></button><button onClick={() => removePackage(p)} title="Delete" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={14} /></button></td></tr>)}
          {!packages.length && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No packages yet</td></tr>}
        </tbody></table></div>
      </div>
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Description</th><th>Default Price</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {services.map(s => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 600 }}>{s.name}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{s.description || '-'}</td>
                  <td>{s.currency} {Number(s.defaultPrice || 0).toLocaleString()}</td>
                  <td><span className={`badge ${s.isActive ? 'badge-green' : 'badge-gray'}`}>{s.isActive ? 'Active' : 'Inactive'}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => setModal({ service: s })} title="Edit" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><Pencil size={14} /></button>
                      <button onClick={() => remove(s)} title="Delete" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!services.length && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 30 }}>No services yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Services;
